import { SetFiltersGetter } from './interfaces/SetFiltersGetter';
import { Http } from './Http';
import { PaginatedResponse } from './interfaces/PaginatedResponse';
import { ResourceFilters } from './interfaces/ResourceFilters';
import { ODataQueryBuilder } from './ODataQueryBuilder';
import { IsNotEqualResourceFilter } from './types/IsNotEqualResourceFilter';
import { NetworkError } from './models/NetworkError';
import { AxiosError } from 'axios';
import { useLogs } from '~~/composables/useLogs';

export default class Resource {
  // #region class properties
  private queries: Record<string, any> = {};
  private filters: ResourceFilters = {
    contains: {},
    isEqual: {},
    isNotEqual: {},
    hasAnyWhere: {},
    isLessThan: {},
    isGreaterThan: {},
    or: [],
    isAny: {},
    custom: ''
  };

  private originalEndpoint = '';
  protected endpoint = '/';

  public id = '';
  public resourceName = '';
  // #endregion

  // #region endpoint resolvers
  protected resolveEndpoint(): void {
    if (!this.originalEndpoint) {
      this.originalEndpoint = this.endpoint;
    } else {
      this.endpoint = this.originalEndpoint;
    }

    this.resolveQueries();
    this.resolveFilters();
  }

  protected updateEndpointForSlug(id?: string, slug?: string): void {
    const endPointSlug = slug ? `/${slug}` : '';
    const endPointId = id ? `/${id}` : '';

    this.endpoint = `${this.endpoint}${endPointId}${endPointSlug}`;
  }

  protected resolveQueries(): void {
    Object.entries(this.queries).forEach(([key, value], index) => {
      const prefix = index === 0 ? '?' : '&';
      this.endpoint += `${prefix}${key}=${value}`;
    });
  }

  protected resolveFilters(): void {
    const prefix = !Object.keys(this.queries).length ? '?' : '&';
    this.endpoint += new ODataQueryBuilder(this.filters).getQuery(prefix);
  }
  // #endregion

  // #region data hydration
  public hydrate(data: this): this {
    Object.entries(data).forEach(([key, value]) => {
      this[key as keyof this] = value;
    });
    return this;
  }
  // #endregion

  // #region queries/filters
  public setQueries(queries: Record<string, any>): this {
    this.queries = { ...queries, ...this.queries };
    return this;
  }

  protected contains(filters: Record<string, string | number | null> | null): this {
    this.filters.contains = { ...filters, ...this.filters.contains };
    return this;
  }

  protected isEqual(filters: Record<string, string | number | boolean | null> | null): this {
    this.filters.isEqual = { ...filters, ...this.filters.isEqual };
    return this;
  }

  protected isNotEqual(filters: IsNotEqualResourceFilter): this {
    this.filters.isNotEqual = { ...filters, ...this.filters.isNotEqual };
    return this;
  }

  protected hasAnyWhere(filters: Record<string, Record<string, any>>): this {
    this.filters.hasAnyWhere = { ...filters, ...this.filters.hasAnyWhere };
    return this;
  }

  protected isLessThan(filters: Record<string, string | number | boolean | null> | null): this {
    this.filters.isLessThan = { ...filters, ...this.filters.isLessThan };
    return this;
  }

  protected isGreaterThan(filters: Record<string, string | number | boolean | null> | null): this {
    this.filters.isGreaterThan = { ...filters, ...this.filters.isGreaterThan };
    return this;
  }

  protected or(filters: Array<Record<string, any>>): this {
    this.filters.or = filters;
    return this;
  }
  protected isAny(filters: Record<string, string | number | boolean | null> | null): this {
    this.filters.isAny = { ...filters, ...this.filters.isEqual };
    return this;
  }
  protected custom(filters: string): this {
    this.filters.custom = filters;
    return this;
  }

  public get setFilters(): SetFiltersGetter<this> {
    return {
      contains: (filters: Record<string, string | number | null> | null) => this.contains(filters),
      isEqual: (filters: Record<string, string | number | boolean | null> | null) =>
        this.isEqual(filters),
      isNotEqual: (filters: IsNotEqualResourceFilter) => this.isNotEqual(filters),
      hasAnyWhere: (filters: Record<string, Record<string, any>>) => this.hasAnyWhere(filters),
      isLessThan: (filters: Record<string, string | number | boolean | null> | null) =>
        this.isLessThan(filters),
      isGreaterThan: (filters: Record<string, string | number | boolean | null> | null) =>
        this.isGreaterThan(filters),
      or: (filters) => this.or(filters),
      isAny: (filters: Record<string, string | number | boolean | null> | null) =>
        this.isAny(filters),
      custom: (filters) => this.custom(filters)
    };
  }
  // #endregion

  // #region HTTP methods
  // HTTP GET
  public async list(): Promise<PaginatedResponse<this>> {
    this.resolveEndpoint();
    try {
      const response = await new Http().apiServer().get(this.endpoint);

      return response.data as unknown as PaginatedResponse<this>;
    } catch (error) {
      await new NetworkError(error as AxiosError).handle();
      useLogs().trackException(error as Error);
      throw error;
    }
  }

  // HTTP GET
  public async get<type>(): Promise<type> {
    this.endpoint = `${this.endpoint}`;
    this.resolveEndpoint();

    try {
      const response = await new Http().apiServer().get<type>(this.endpoint);
      return response.data;
    } catch (error) {
      await new NetworkError(error as AxiosError).handle();
      useLogs().trackException(error as Error);
      throw error;
    }
  }

  // HTTP GET /{ID}
  public async find(id?: string): Promise<this> {
    this.endpoint = `${this.endpoint}/${id || this.id}`;
    this.resolveEndpoint();

    try {
      const response = await new Http().apiServer().get(this.endpoint);
      return response.data;
    } catch (error) {
      await new NetworkError(error as AxiosError).handle();
      useLogs().trackException(error as Error);
      throw error;
    }
  }

  // HTTP POST
  public async create(payload: Record<string, any>): Promise<this> {
    this.resolveEndpoint();
    try {
      const response = await new Http().apiServer().post(this.endpoint, payload);
      return response.data;
    } catch (error) {
      await new NetworkError(error as AxiosError).handle();
      useLogs().trackException(error as Error);
      throw error;
    }
  }

  //HTTP FORM DATA
  public async createFormData(formData: FormData): Promise<this> {
    this.resolveEndpoint();
    try {
      const response = await new Http().apiServer().post(this.endpoint, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      return response.data;
    } catch (error) {
      await new NetworkError(error as AxiosError).handle();
      useLogs().trackException(error as Error);
      throw error;
    }
  }
  // HTTP PUT /{ID}
  public async update(payload: Record<string, any>, id?: string, slug?: string): Promise<this> {
    if (id || slug) {
      this.updateEndpointForSlug(id, slug);
    }
    this.resolveEndpoint();

    try {
      const response = await new Http().apiServer().put(this.endpoint, payload);
      return response.data;
    } catch (error) {
      await new NetworkError(error as AxiosError).handle();
      useLogs().trackException(error as Error);
      throw error;
    }
  }

  // HTTP DELETE /{ID}
  public async delete(id?: string): Promise<void> {
    this.endpoint = `${this.endpoint}/${id || this.id}`;
    this.resolveEndpoint();

    try {
      const response = await new Http().apiServer().delete(this.endpoint);
      return response.data;
    } catch (error) {
      await new NetworkError(error as AxiosError).handle();
      useLogs().trackException(error as Error);
      throw error;
    }
  }
  // #endregion
  public async withTempEndpoint<T>(endpoint: string, fn: () => Promise<T>): Promise<T> {
    const original = this.endpoint;
    this.endpoint = endpoint;

    try {
      return await fn();
    } finally {
      this.endpoint = original;
    }
  }
}
